import { useRef, useState } from 'react';

import { Empty } from '../../components/ui/bits.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { DOCS } from '../../lib/exercises.js';
import { fmtBytes } from '../../lib/format.js';
import * as api from '../../lib/api.js';
import { Icon } from '../../components/ui/Icon.jsx';
import { t } from '../../i18n/index.js';

const IMG = /\.(jpe?g|png|gif|webp|heic)(\?|$)/i;

/*
  Dokumenti tečajnika.

  Novo: fotografije se pred nalaganjem pomanjšajo (glej api.compressImage).
  Telefon posname 4–12 MB sliko; za berljivo osebno izkaznico je dovolj
  2000 px in rezultat pod 500 kB. Na terenu z mobilnim omrežjem je bilo
  nalaganje izvirnih fotografij pogosto neuspešno.
*/

export function Documents({ uid, docs, onChanged, readOnly = false }) {
  const [preview, setPreview] = useState(null);
  const count = Object.keys(docs).length;

  return (
    <div className="stack stack--loose">
      <section className="card card--brand card--pad row">
        <Icon name="idCard" size={26} className="t-brand" />
        <div className="spacer">
          <h2 className="t-lg">{t('Dokumentacija')}</h2>
          <p className="t-sm t-dim">
            {t('Naloži fotografije. Vidita jih samo ti in inštruktor.')}
          </p>
        </div>
        <span
          className={`t-black num ${count === DOCS.length ? 't-ok' : 't-brand'}`}
          style={{ fontSize: 'var(--fs-2xl)' }}
        >
          {count}/{DOCS.length}
        </span>
      </section>

      <div className="stack">
        {DOCS.map((doc) => (
          <DocCard
            key={doc.id}
            doc={doc}
            state={docs[doc.id]}
            uid={uid}
            onChanged={onChanged}
            onPreview={setPreview}
            readOnly={readOnly}
          />
        ))}
      </div>

      {count === 0 && (
        <Empty
          icon="file"
          hint={t('Zdravniško spričevalo je eden od pogojev za pristop k izpitu.')}
        />
      )}

      {preview && <DocPreview doc={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function DocCard({ doc, state, uid, onChanged, onPreview, readOnly }) {
  const { toast, confirm } = useUI();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      await api.uploadDoc(uid, doc.id, file);
      toast.ok(t('{doc} je naložen.', { doc: t(doc.label) }));
      await onChanged();
    } catch (e) {
      toast.bad(e.message ?? t('Nalaganje ni uspelo.'));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  };

  const izbrisi = async () => {
    const ok = await confirm({
      title: t('Izbrišem {doc}?', { doc: t(doc.label).toLowerCase() }),
      body: t('Fotografija bo trajno odstranjena. Naložiš jo lahko znova.'),
      confirmLabel: t('Izbriši'),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.deleteDoc(uid, state);
      toast.ok(t('Dokument je izbrisan.'));
      await onChanged();
    } catch (e) {
      toast.bad(e.message ?? t('Brisanje ni uspelo.'));
    } finally {
      setBusy(false);
    }
  };

  const isImage = state && IMG.test(state.name ?? '');

  return (
    <section className={`card${state ? ' card--ok' : ''}`}>
      <div className="card__body row row--wrap doc">
        <Icon name={doc.id === 'zdravniski' ? 'file' : 'idCard'} size={24} className="t-dim" />

        <div className="spacer">
          <h3 className="t-base">{t(doc.label)}</h3>
          {busy ? (
            <p className="t-sm t-wait" role="status">
              {t('Nalagam …')}
            </p>
          ) : state ? (
            <p className="row t-sm t-ok" style={{ gap: 'var(--s-1)' }}>
              <Icon name="check" size={14} /> {t('Naloženo')}
              {state.uploadedAt ? ` · ${state.uploadedAt}` : ''}
              {state.size ? ` · ${fmtBytes(state.size)}` : ''}
            </p>
          ) : (
            <p className="t-sm t-dimmer">{t('Ni naloženo')}</p>
          )}
        </div>

        <div className="row doc__actions" style={{ gap: 'var(--s-2)' }}>
          {state && !busy && (
            <button className="btn btn--sm btn--secondary" onClick={() => onPreview(state)}>
              {t('Oglej')}
            </button>
          )}
          {!readOnly && (
            <button
              className={`btn btn--sm ${state ? 'btn--ghost' : 'btn--primary'}`}
              onClick={() => input.current?.click()}
              disabled={busy}
            >
              {state ? t('Zamenjaj') : t('Naloži')}
            </button>
          )}

          {/* Fotografija osebne izkaznice je tvoj podatek — izbrišeš jo lahko
              kadarkoli sam, brez prošnje inštruktorju. */}
          {state && !readOnly && !busy && (
            <button
              className="btn-icon btn-icon--sm"
              onClick={izbrisi}
              aria-label={t('Izbriši {doc}', { doc: t(doc.label) })}
              title={t('Izbriši')}
            >
              <Icon name="trash" size={16} />
            </button>
          )}
          <input
            ref={input}
            type="file"
            accept="image/*,application/pdf"
            hidden
            aria-label={t('Naloži: {doc}', { doc: t(doc.label) })}
            onChange={(e) => pick(e.target.files?.[0])}
          />
        </div>
      </div>

      {state && !busy && isImage && (
        <button
          onClick={() => onPreview(state)}
          style={{ display: 'block', width: '100%', padding: 0 }}
          aria-label={t('Povečaj: {doc}', { doc: t(doc.label) })}
        >
          <img
            src={state.url}
            alt=""
            style={{ width: '100%', maxHeight: 200, objectFit: 'cover' }}
          />
        </button>
      )}
    </section>
  );
}

function DocPreview({ doc, onClose }) {
  const isImage = IMG.test(doc.name ?? '');
  return (
    <Modal
      title={doc.name}
      onClose={onClose}
      footer={
        <a
          className="btn btn--primary"
          href={doc.url}
          target="_blank"
          rel="noopener noreferrer"
          data-autofocus
        >
          <Icon name="right" size={16} /> {t('Odpri v novem zavihku')}
        </a>
      }
    >
      {isImage ? (
        <img
          src={doc.url}
          alt={doc.name}
          style={{ width: '100%', borderRadius: 'var(--r-md)' }}
        />
      ) : (
        <Empty icon="file" title={doc.name} hint={t('Dokument se odpre v novem zavihku.')} />
      )}
    </Modal>
  );
}
